import { Alert, InputAdornment, MenuItem, Skeleton, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useField } from "formik";
import { useSnackbar } from "notistack";
import { Fragment } from "react";
import { NumericFormat, useNumericFormat } from "react-number-format";
import { CertificateLimitsResponse } from "Services/api/request/interfaces";
import { getLiquidationChoices } from "Services/api/request/request";
import { BankAccountFields, BankAccountFieldsSchema } from "Shared/BankAccountFields/BankAccountFields";
import { FormikMoney, FormikMoneyField, numericConfig } from "Shared/FormikMoney/FormikMoney";
import FormikSelect, { FormikSelectProps } from "Shared/FormikSelect/FormikSelect";
import { FormikSlider } from "Shared/FormikSlider/FormikSlider";
import FormikTextField from "Shared/FormikTextField/FormikTextField";
import { Query } from "Shared/Query/Query";
import { SavingsAccountSelect } from "Shared/SavingAccountSelect/SavingAccountSelect";
import { TypeOfPayment } from "Shared/TypeOfPayment/TypeOfPayment";
import { requiredNumberValidation, requiredValidation } from "Utils/validations";
import * as Yup from "yup";

export function CertificateForm({
  options,
  isRegistration = false,
}: {
  options: CertificateLimitsResponse["data"];
  isRegistration?: boolean;
}) {
  const { minTimeLimit, maxTimeLimit } = options;
  const [minTerm, maxTerm] = [minTimeLimit, maxTimeLimit];

  return (
    <Fragment>
      <FormikMoney
        name="amount"
        id="amount"
        label="Monto de certificado"
        helperText="Monto minimo: RD$5,000"
        required
      />
      <FormikSlider
        id="term"
        name="term"
        label="Plazo"
        aria-label="Plazo"
        valueLabelDisplay="auto"
        min={minTerm}
        max={maxTerm}
        step={1}
        marks
        formatValue={(value) => `${value} meses`}
      />

      <Interest options={options} />
      <LiquidationSelect isRegistration={isRegistration} />
      <SavingsAccount isRegistration={isRegistration} />
      <TransferAccount isRegistration={isRegistration} />
      <TypeOfPayment
        label="Forma de depósito"
        help="Método por el cual la cooperativa recibirá los fondos de la inversión solicitada."
      />
    </Fragment>
  );
}

function SavingsAccount({ isRegistration }: { isRegistration: boolean }) {
  const [{ value: liquidation }] = useField<string>("liquidation");

  if (isRegistration || liquidation !== "withdrawable") return null;

  return <SavingsAccountSelect name="savingsAccount" label="Cuenta a depositar" include={["in_sight"]} />;
}

function TransferAccount({ isRegistration }: { isRegistration: boolean }) {
  const [{ value: liquidation }] = useField<string>("liquidation");

  if (liquidation !== "liquidable") return null;

  return <BankAccountFields accountField="account" showAccountField={!isRegistration} label="Cuenta a transferir" />;
}

function LiquidationSelect({ isRegistration }: { isRegistration: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const [{ value: account }, , { setValue: setAccount }] = useField<string>("account");
  const [, , { setValue: setLiquidation }] = useField<string>("liquidation");

  const result = useQuery({
    queryKey: [getLiquidationChoices.name],
    queryFn: async () => {
      try {
        const response = await getLiquidationChoices();
        return response.data;
      } catch (error) {
        enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
        console.error(error);
        throw error;
      }
    },
  });

  const props: FormikSelectProps<string> = {
    id: "liquidation",
    name: "liquidation",
    label: "Forma de liquidación",
    SelectDisplayProps: { "aria-label": "Forma de liquidación" },
    required: true,
    onChange: (ev) => {
      const value = ev.target.value;
      setLiquidation(value);
      if (value === "liquidable" && isRegistration) setAccount("new");
      else if (value !== "liquidable" && account) setAccount("");
    },
    help: (
      <Alert severity="info" color="success" variant="outlined">
        Método mediante el cual deseas recibir los intereses generados por tu inversión:
        <ul style={{ margin: 0 }}>
          <li>Retirables: Se depositarán en tu cuenta de ahorros de la cooperativa.</li>
          <li>Liquidables: Serán transferidos a tu cuenta bancaria en otra institución financiera.</li>
          <li>Capitalizables: Se sumarán directamente al monto de tu certificado de inversión.</li>
          <li>A vencimiento: Se entregarán en tu cuenta de la cooperativa al finalizar el plazo de la inversión.</li>
        </ul>
      </Alert>
    ),
  };

  return (
    <Query
      result={result}
      OnLoading={() => (
        <Stack spacing={1} width="100%">
          <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        </Stack>
      )}
      onError={() => <FormikSelect {...props} />}
      onSuccess={(liquidation) => (
        <FormikSelect {...props}>
          {liquidation.map(([value, display]) => (
            <MenuItem key={value} value={value}>
              {display}
            </MenuItem>
          ))}
        </FormikSelect>
      )}
    />
  );
}

function Interest({ options: { description: rates } }: { options: CertificateLimitsResponse["data"] }) {
  const [{ value: amount }] = useField<FormikMoneyField>("amount");
  const [{ value: term }] = useField<number>("term");
  const [{ value: liquidation }] = useField<string>("liquidation");

  const { format } = useNumericFormat<typeof FormikTextField>({ ...numericConfig, fixedDecimalScale: true });
  let interestRate = 0;
  let interest = 0;

  function calculateInterest(amount: number, interestRate: number) {
    const monthlyInterest = (interestRate / 100 / 360) * 30;
    return amount * monthlyInterest * 0.9;
  }

  if (amount)
    for (const { min, max, minTimeLimit, maxTimeLimit, capitalizedInterestRate, otherWayInterestRate } of rates) {
      const interestRateValue = liquidation === "capitalizable" ? capitalizedInterestRate : otherWayInterestRate;
      if (interestRateValue > interestRate) interestRate = interestRateValue;
      if (amount >= min && amount <= max && term >= minTimeLimit && term <= maxTimeLimit) {
        interestRate = interestRateValue;
        interest = calculateInterest(amount, interestRate);
        break;
      }
      interest = calculateInterest(amount, interestRate);
    }

  return (
    <Fragment>
      <FormikTextField
        id="rate"
        name="rate"
        label="Tasa anual"
        type="number"
        value={interestRate.toString()}
        variant="outlined"
        disabled
      />
      <NumericFormat
        name="interest"
        id="interest"
        fullWidth
        label="Interes mensual a percibir"
        variant="outlined"
        value={format(interest.toString())}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        customInput={FormikTextField}
        disabled
        {...numericConfig}
      />
    </Fragment>
  );
}

export function CertificateFormSkeleton() {
  return (
    <Stack width={"100%"} spacing={2}>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Stack spacing={1} width="100%">
        <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
        <Skeleton variant="rectangular" width={"100%"} height={56} />
      </Stack>
      <Skeleton variant="rectangular" width={"100%"} height={37} />
    </Stack>
  );
}

export function useCertificateFormSchema({
  options: { minTimeLimit, maxTimeLimit, minimumBalance },
}: {
  options: CertificateLimitsResponse["data"];
}) {
  const { format } = useNumericFormat<typeof FormikTextField>({ ...numericConfig, fixedDecimalScale: true });
  const [minTerm, maxTerm] = [minTimeLimit, maxTimeLimit];
  const bankAccountSchema = BankAccountFieldsSchema("account");

  return Yup.object({
    amount: requiredNumberValidation
      .nullable()
      .min(minimumBalance, `Monto tiene que ser mayor que ${format(minimumBalance.toString())}`),
    term: Yup.number()
      .required("Requerido")
      .integer("Debe ser un entero")
      .positive("Debe ser un entero positivo")
      .min(minTerm, `No menos de ${minTerm} meses`)
      .max(maxTerm, `No mas de ${maxTerm} meses`),
    liquidation: requiredValidation,
    typeofPayment: requiredValidation,
    account: Yup.string().when("typeofPayment", {
      is: "liquidable",
      then: () => requiredValidation,
    }),
  }).concat(bankAccountSchema);
}
